import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import api from '@/services/api';

export interface PaymentMethod {
  card_name: string;
  card_last4: string;
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  credits: number;
  last_login: string | null;
  display_name?: string | null;
  phone: string | null;
  is_phone_verified: boolean;
  created_at: string | null;
  total_order_amount: number;
  payment_method: PaymentMethod | null;
  social_kakao: boolean;
  social_naver: boolean;
  social_google: boolean;
  workbook_count: number;
  question_count: number;
  passage_count: number;
  marketing_consent: {
    sms: 'Y' | 'N' | null;
    email: 'Y' | 'N' | null;
    deferred_until: string | null;
  };
}

export interface AdminUsersPageResponse {
  users: AdminUser[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  preferences: {
    admin_visible_columns?: string[];
  };
  lookups: Record<string, unknown>;
}

export interface AdminUsersPageParams {
  search?: string;
  page: number;
  per_page: number;
  role?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface KPIs {
  total_users: number;
  new_users: number;
  period_revenue: number;
  gross_revenue: number;
  order_refund_amount: number;
  refund_estimated_amount: number;
  refund_undated_amount: number;
  total_revenue: number;
  revenue_change_pct: number | null;
  revenue_yoy_change_pct: number | null;
  active_subscriptions: number;
  active_subscription_users: number;
  billable_active_subscriptions: number;
  billable_active_subscription_users: number;
  stale_active_subscriptions: number;
  stale_active_subscription_users: number;
  active_missing_billing_key: number;
  new_subscriptions: number;
  pending_orders: number;
  raw_pending_orders: number;
  stale_pending_orders: number;
  refund_amount: number;
  avg_order_value: number;
  paid_order_count: number;
}

export interface RevenueChartItem {
  label: string;
  revenue: number;
  orders: number;
}

export interface RevenueByProductType {
  product_type: string;
  count: number;
  revenue: number;
}

export interface RevenueByPaymentMethod {
  payment_method: string;
  count: number;
  revenue: number;
}

export interface SubscriptionHealth {
  mrr: number;
  arr: number;
  churned: number;
  raw_churned: number;
  maintenance_churned: number;
  new_subscriptions: number;
  raw_active: number;
  raw_active_users: number;
  billable_active: number;
  billable_active_users: number;
  stale_active: number;
  stale_active_users: number;
  active_missing_billing_key: number;
  on_hold: number;
  unresolved_on_hold: number;
  churn_rate: number;
}

export interface UserActivity {
  dau: number;
  wau: number;
  mau: number;
  zero_credit_users: number;
  avg_credits: number;
}

export interface CreditUsageByTask {
  task: string;
  count: number;
  credits: number;
}

export interface CreditUsage {
  total_consumed: number;
  total_purchased: number;
  by_task: CreditUsageByTask[];
}

export interface DashboardRecentOrder {
  id: number;
  order_number: string;
  username: string;
  display_name?: string;
  amount: number;
  refund_amount: number;
  status: string;
  created_at: string;
  product_name: string | null;
}

export interface DashboardData {
  period_days: number;
  filter_month: { year: number; month: number } | null;
  kpis: KPIs;
  revenue_chart: RevenueChartItem[];
  revenue_by_product_type: RevenueByProductType[];
  revenue_by_payment_method: RevenueByPaymentMethod[];
  subscription_health: SubscriptionHealth;
  recent_orders: DashboardRecentOrder[];
  user_activity: UserActivity;
  credit_usage: CreditUsage;
  preferences?: {
    admin_dashboard_collapsed?: string[];
  };
}

export interface DashboardPageParams {
  start_date?: string;
  end_date?: string;
  period?: number;
  year?: number;
  month?: number;
}

export interface ReferrerInfo {
  id: number;
  username: string;
  display_name?: string | null;
  email?: string | null;
}

export interface AdminUserDetail {
  id: number;
  wp_user_id: number;
  username: string;
  email: string;
  role: string;
  display_name: string | null;
  phone: string | null;
  is_phone_verified: boolean;
  credits: number;
  created_at: string | null;
  last_login: string | null;
  social_google_id: string | null;
  social_naver_id: string | null;
  social_kakao_id: string | null;
  membership_type: string | null;
  membership_expires: string | null;
  has_billing_key: boolean;
  has_password: boolean;
  total_order_amount: number;
  payment_method: PaymentMethod | null;
  referred_by: number | null;
  referrer_info: ReferrerInfo | null;
  users_referred_count: number;
  total_referral_points_earned: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
}

export interface RecentOrder {
  id: number;
  order_number: string;
  status: string;
  total_amount: number;
  created_at: string | null;
  products: string;
  payment_method: string;
  toss_payment_method?: string | null;
}

export interface SubscriptionPaymentHistoryItem {
  order_id: number;
  order_number: string;
  status: string;
  total_amount: number;
  refund_amount: number;
  paid_at: string | null;
  created_at: string | null;
}

export interface Subscription {
  id: number;
  status: string;
  product_name: string | null;
  product_slug: string | null;
  amount: number;
  billing_key: string | null;
  has_billing_key: boolean;
  next_payment_date: string | null;
  refund_order_id: number | null;
  refund_order_number: string | null;
  refund_order_status: string | null;
  refund_total_amount: number | null;
  refund_amount: number;
  max_refundable: number;
  can_cancel_and_refund: boolean;
  is_representative: boolean;
  created_at: string | null;
  payment_history?: SubscriptionPaymentHistoryItem[];
}

export interface CreditHistory {
  id: number;
  amount: number;
  balance_after: number;
  description: string | null;
  transaction_type: string | null;
  sheet_name: string | null;
  passage_id: string | null;
  task: string | null;
  source?: string | null;
  resource_name?: string | null;
  generation_item_id?: number | null;
  mocktest_generation_item_id?: number | null;
  single_question_generation_item_id?: number | null;
  metadata_json?: Record<string, unknown> | null;
  created_at: string | null;
}

export interface FileUsage {
  id: number;
  filename: string;
  file_type: string;
  price: number;
  created_at: string | null;
}

export interface LoginHistoryItem {
  id: number;
  success: boolean;
  failure_reason: string | null;
  login_type: string;
  client_platform: string;
  app_version: string | null;
  ip_address: string;
  created_at: string | null;
}

export interface AdminUserDetailPageResponse {
  user: AdminUserDetail;
  recent_orders: PaginatedResponse<RecentOrder>;
  subscriptions: Subscription[];
  recent_credits: PaginatedResponse<CreditHistory>;
  file_usage: PaginatedResponse<FileUsage>;
  login_history: LoginHistoryItem[];
  lookups: Record<string, unknown>;
}

export interface AdminUserDetailPageParams {
  order_page: number;
  credit_page: number;
  credit_per_page: number;
  file_page: number;
}

export type AdminOrderListMode = 'all' | 'refunds';

export interface OrderAttribution {
  source_type?: string;
  utm_source?: string;
  utm_medium?: string;
  device_type?: string;
}

export interface RefundRequestInfo {
  status?: string;
  reason?: string;
  requested_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
  processed_at?: string;
}

export interface AdminOrder {
  id: number;
  order_number: string;
  user_id: number;
  username: string;
  display_name: string | null;
  amount: number;
  status:
    | 'pending'
    | 'awaiting_deposit'
    | 'paid'
    | 'refund_requested'
    | 'refund_rejected'
    | 'expired'
    | 'payment_failed'
    | 'partially_refunded'
    | 'refunded'
    | 'cancelled';
  payment_method: string | null;
  paid_at: string | null;
  created_at: string;
  product_name: string | null;
  product_type: string | null;
  failure_code: string | null;
  failure_message?: string | null;
  toss_payment_method?: string | null;
  attribution: OrderAttribution | null;
  refund_request?: RefundRequestInfo | null;
}

export interface AdminOrdersPageParams {
  search?: string;
  page: number;
  per_page: number;
  refund_request_status?: string;
  hide_inactive?: boolean;
}

export interface AdminOrdersPageResponse {
  orders: AdminOrder[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  lookups: Record<string, unknown>;
}

interface AdminRequestConfig extends AxiosRequestConfig {
  skipRetry?: boolean;
}

function isMissingBffEndpoint(error: unknown): boolean {
  return axios.isAxiosError(error) && [404, 405].includes(error.response?.status ?? 0);
}

async function adminGet<T>(url: string, config?: AdminRequestConfig): Promise<T> {
  const response = await api.get<T>(url, {
    ...(config ?? {}),
    skipRetry: true,
  } as AdminRequestConfig);

  return response.data;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function asRecord(value: unknown): Record<string, any> {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  return value && typeof value === 'object' ? (value as Record<string, any>) : {};
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asNumber(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

function normalizeDashboardData(raw: unknown): DashboardData {
  const root = asRecord(raw);
  const payload = asRecord(root.dashboard ?? root.data ?? raw);
  const summary = asRecord(payload.summary ?? payload.kpis);
  const charts = asRecord(payload.charts);
  const subscriptionHealth = asRecord(payload.subscription_health);
  const userActivity = asRecord(payload.user_activity);
  const creditUsage = asRecord(payload.credit_usage);
  const preferences = asRecord(root.preferences ?? payload.preferences);

  return {
    period_days: payload.period_days ?? 30,
    filter_month: payload.filter_month ?? null,
    kpis: {
      total_users: summary.total_users ?? 0,
      new_users: summary.new_users ?? 0,
      period_revenue: summary.period_revenue ?? 0,
      gross_revenue: summary.gross_revenue ?? summary.period_revenue ?? 0,
      order_refund_amount: summary.order_refund_amount ?? 0,
      refund_estimated_amount: summary.refund_estimated_amount ?? 0,
      refund_undated_amount: summary.refund_undated_amount ?? 0,
      total_revenue: summary.total_revenue ?? 0,
      revenue_change_pct: summary.revenue_change_pct ?? null,
      revenue_yoy_change_pct: summary.revenue_yoy_change_pct ?? null,
      active_subscriptions: summary.active_subscriptions ?? 0,
      active_subscription_users:
        summary.active_subscription_users ?? summary.active_subscriptions ?? 0,
      billable_active_subscriptions:
        summary.billable_active_subscriptions ?? summary.active_subscriptions ?? 0,
      billable_active_subscription_users:
        summary.billable_active_subscription_users ??
        summary.billable_active_subscriptions ??
        summary.active_subscriptions ??
        0,
      stale_active_subscriptions: summary.stale_active_subscriptions ?? 0,
      stale_active_subscription_users:
        summary.stale_active_subscription_users ?? summary.stale_active_subscriptions ?? 0,
      active_missing_billing_key: summary.active_missing_billing_key ?? 0,
      new_subscriptions: summary.new_subscriptions ?? 0,
      pending_orders: summary.pending_orders ?? 0,
      raw_pending_orders: summary.raw_pending_orders ?? summary.pending_orders ?? 0,
      stale_pending_orders: summary.stale_pending_orders ?? 0,
      refund_amount: summary.refund_amount ?? 0,
      avg_order_value: summary.avg_order_value ?? 0,
      paid_order_count: summary.paid_order_count ?? 0,
    },
    revenue_chart: asArray<RevenueChartItem>(payload.revenue_chart ?? charts.revenue_chart),
    revenue_by_product_type: asArray<RevenueByProductType>(
      payload.revenue_by_product_type ?? charts.revenue_by_product_type
    ),
    revenue_by_payment_method: asArray<RevenueByPaymentMethod>(
      payload.revenue_by_payment_method ?? charts.revenue_by_payment_method
    ),
    subscription_health: {
      mrr: subscriptionHealth.mrr ?? 0,
      arr: subscriptionHealth.arr ?? 0,
      churned: subscriptionHealth.churned ?? 0,
      raw_churned: subscriptionHealth.raw_churned ?? subscriptionHealth.churned ?? 0,
      maintenance_churned: subscriptionHealth.maintenance_churned ?? 0,
      new_subscriptions: subscriptionHealth.new_subscriptions ?? 0,
      raw_active: subscriptionHealth.raw_active ?? summary.active_subscriptions ?? 0,
      raw_active_users:
        subscriptionHealth.raw_active_users ??
        summary.active_subscription_users ??
        summary.active_subscriptions ??
        0,
      billable_active:
        subscriptionHealth.billable_active ??
        summary.billable_active_subscriptions ??
        summary.active_subscriptions ??
        0,
      billable_active_users:
        subscriptionHealth.billable_active_users ??
        summary.billable_active_subscription_users ??
        summary.billable_active_subscriptions ??
        summary.active_subscriptions ??
        0,
      stale_active: subscriptionHealth.stale_active ?? 0,
      stale_active_users:
        subscriptionHealth.stale_active_users ??
        summary.stale_active_subscription_users ??
        summary.stale_active_subscriptions ??
        0,
      active_missing_billing_key: subscriptionHealth.active_missing_billing_key ?? 0,
      on_hold: subscriptionHealth.on_hold ?? 0,
      unresolved_on_hold: subscriptionHealth.unresolved_on_hold ?? 0,
      churn_rate: asNumber(subscriptionHealth.churn_rate),
    },
    recent_orders: asArray<DashboardRecentOrder>(payload.recent_orders),
    user_activity: {
      dau: asNumber(userActivity.dau),
      wau: asNumber(userActivity.wau),
      mau: asNumber(userActivity.mau),
      zero_credit_users: asNumber(userActivity.zero_credit_users),
      avg_credits: asNumber(userActivity.avg_credits),
    },
    credit_usage: {
      total_consumed: asNumber(creditUsage.total_consumed),
      total_purchased: asNumber(creditUsage.total_purchased),
      by_task: asArray<CreditUsageByTask>(creditUsage.by_task),
    },
    preferences: Object.keys(preferences).length > 0
      ? { admin_dashboard_collapsed: asArray<string>(preferences.admin_dashboard_collapsed) }
      : undefined,
  };
}

function normalizeUsersPageData(raw: unknown): AdminUsersPageResponse {
  const root = asRecord(raw);
  const payload = asRecord(root.list ?? root.page_data ?? root.data ?? raw);
  const pagination = asRecord(payload.pagination);
  const preferences = asRecord(
    root.preferences ??
      payload.preferences ??
      root.meta?.preferences ??
      payload.meta?.preferences
  );

  return {
    users: asArray<AdminUser>(payload.users ?? payload.items),
    total: payload.total ?? pagination.total ?? 0,
    page: payload.page ?? pagination.page ?? 1,
    per_page: payload.per_page ?? pagination.per_page ?? 20,
    pages: payload.pages ?? pagination.pages ?? 1,
    preferences: {
      admin_visible_columns: asArray<string>(
        preferences.admin_visible_columns ?? preferences.visible_columns
      ),
    },
    lookups: asRecord(root.lookups ?? payload.lookups ?? root.meta?.lookups ?? payload.meta?.lookups),
  };
}

function normalizeUserDetailPageData(raw: unknown): AdminUserDetailPageResponse {
  const root = asRecord(raw);
  const detail = asRecord(root.detail);
  const related = asRecord(root.related);
  if (Object.keys(detail).length > 0 || Object.keys(related).length > 0) {
    return {
      user: detail as AdminUserDetail,
      recent_orders: asRecord(related.recent_orders) as PaginatedResponse<RecentOrder>,
      subscriptions: asArray<Subscription>(related.subscriptions),
      recent_credits: asRecord(related.recent_credits) as PaginatedResponse<CreditHistory>,
      file_usage: asRecord(related.file_usage) as PaginatedResponse<FileUsage>,
      login_history: asArray<LoginHistoryItem>(related.login_history),
      lookups: asRecord(root.lookups),
    };
  }

  const payload = asRecord(root.page_data ?? root.data ?? raw);

  return {
    user: asRecord(payload.user) as AdminUserDetail,
    recent_orders: asRecord(payload.recent_orders) as PaginatedResponse<RecentOrder>,
    subscriptions: asArray<Subscription>(payload.subscriptions),
    recent_credits: asRecord(payload.recent_credits) as PaginatedResponse<CreditHistory>,
    file_usage: asRecord(payload.file_usage) as PaginatedResponse<FileUsage>,
    login_history: asArray<LoginHistoryItem>(payload.login_history),
    lookups: asRecord(root.lookups ?? payload.lookups ?? root.meta?.lookups ?? payload.meta?.lookups),
  };
}

function normalizeOrdersPageData(raw: unknown): AdminOrdersPageResponse {
  const root = asRecord(raw);
  const payload = asRecord(root.list ?? root.page_data ?? root.data ?? raw);
  const pagination = asRecord(payload.pagination);

  return {
    orders: asArray<AdminOrder>(payload.orders ?? payload.items),
    total: payload.total ?? pagination.total ?? 0,
    page: payload.page ?? pagination.page ?? 1,
    per_page: payload.per_page ?? pagination.per_page ?? 20,
    pages: payload.pages ?? pagination.pages ?? 1,
    lookups: asRecord(root.lookups ?? payload.lookups ?? root.meta?.lookups ?? payload.meta?.lookups),
  };
}

export async function getAdminDashboardPage(
  params: DashboardPageParams,
  signal?: AbortSignal
): Promise<DashboardData> {
  try {
    return normalizeDashboardData(
      await adminGet('/api/admin/dashboard-page', {
        params,
        signal,
      })
    );
  } catch (error) {
    if (!isMissingBffEndpoint(error)) {
      throw error;
    }

    return normalizeDashboardData(
      await adminGet('/api/admin/dashboard', {
        params,
        signal,
      })
    );
  }
}

// ── Usage Calculation (SPEC-USAGE-001, T-020) ──────────────────────────────

export interface UsageCalculationQuestionTypeCount {
  question_type: string | null;
  /** Success-only count (sums to success_question_count). */
  count: number;
  /** Cost over ALL rows of the type — failed calls cost real money too. */
  total_cost_usd: number;
  total_cost_krw: number;
  credits_charged_total: number;
  charged_price_krw: number;
}

export interface UsageCalculationModelUsage {
  provider: string;
  model: string;
  input: number;
  output: number;
  cached: number;
  cache_write: number;
  cache_read: number;
  reasoning: number;
  visible_output: number;
  thinking: number;
  calls: number;
  cost_usd: number;
  cost_krw: number;
}

export interface UsageCalculationActiveUser {
  user_id: number;
  username: string | null;
  email: string | null;
  user_display: string | null;
}

export interface UsageCalculationDailyPoint {
  date: string;
  success_count: number;
  total_cost_usd: number;
  total_cost_krw: number;
  credits_charged_total: number;
  charged_price_krw: number;
}

/** Serialized TokenTracker breakdown: provider -> model -> {usage, cost}. */
export interface UsageCalculationTokenBreakdown {
  summary?: Record<string, unknown>;
  claude?: Record<string, UsageCalculationModelTokenEntry>;
  openai?: Record<string, UsageCalculationModelTokenEntry>;
  gemini?: Record<string, UsageCalculationModelTokenEntry>;
}

export interface UsageCalculationModelTokenEntry {
  usage?: Partial<Record<
    'input' | 'output' | 'cached' | 'cache_write' | 'cache_read' |
    'reasoning' | 'visible_output' | 'thinking' | 'calls' | 'attempts',
    number
  >> & {
    /** OpenAI reasoning efforts seen on this model, sorted (may be several
     *  when one request used the same model at different tiers). */
    efforts?: string[];
    /** Claude/Gemini thinking levels seen on this model, sorted. */
    thinking_levels?: string[];
  };
  cost?: number | null;
}

export interface UsageCalculationGenerationLog {
  is_free_sample?: boolean;
  id: number;
  request_id: string | null;
  user_id: number | null;
  user_display: string | null;
  source: string;
  app_version: string | null;
  sheet_type: string | null;
  question_type: string | null;
  difficulty: string | null;
  success: boolean;
  result_status: string;
  ebs_matched: boolean;
  credits_charged: number | null;
  cost_usd: number | null;
  cost_krw: number | null;
  token_breakdown_json: UsageCalculationTokenBreakdown | null;
  models_json: string[] | null;
  metadata_json?: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
}

export interface UsageCalculationImportLog {
  id: number;
  user_id: number | null;
  user_display: string | null;
  source: string;
  import_kind: string;
  filename: string | null;
  file_count: number | null;
  selected_item_count: number | null;
  rows_added: number | null;
  duplicates_count: number | null;
  workbook_id: number | null;
  resource_name: string | null;
  credits_charged: number | null;
  is_premium: boolean;
  status: string | null;
  created_at: string;
}

export interface UsageCalculationCancellationMonitor {
  status: 'no_events' | 'healthy' | 'violation';
  total_count: number;
  correctly_unbilled_count: number;
  invariant_violation_count: number;
  charged_count: number;
  success_flagged_count: number;
  /** Optional so an older backend payload still renders. */
  user_stop_count?: number;
  timeout_count?: number;
  interrupted_count?: number;
  unknown_count?: number;
  last_seen: {
    created_at: string;
    request_id: string | null;
    source: string;
    question_type: string | null;
    cost_krw: number;
    credits_charged: number | null;
  } | null;
}

export interface UsageCalculationDurationProfile {
  stage_label?: string | null;
  provider: string;
  model: string;
  effort: string;
  required_seconds: number;
  source: 'static' | 'p95' | 'p95_stage';
  static_seconds: number;
  p95_seconds: number | null;
  sample_count: number;
}

export interface UsageCalculationDurationPolicy {
  enabled: boolean;
  window_days: number;
  min_samples: number;
  profiles: UsageCalculationDurationProfile[];
}

export interface UsageCalculationData {
  range: { start: string; end: string };
  generation: {
    success_question_count: number;
    total_cost_usd: number;
    total_cost_krw: number;
    success_cost_usd: number;
    success_cost_krw: number;
    failed_cost_usd: number;
    failed_cost_krw: number;
    ebs_matched_count: number;
    ebs_matched_ratio: number;
    credits_charged_total: number;
    by_question_type: UsageCalculationQuestionTypeCount[];
    by_model: UsageCalculationModelUsage[];
    cancellation_monitor: UsageCalculationCancellationMonitor;
    duration_policy: UsageCalculationDurationPolicy;
    active_user_count: number;
    active_users: UsageCalculationActiveUser[];
    recent_logs: UsageCalculationGenerationLog[];
    daily_series: UsageCalculationDailyPoint[];
  };
  import: {
    event_count: number;
    distinct_user_count: number;
    file_count_total: number;
    rows_added_total: number;
    recent_logs: UsageCalculationImportLog[];
  };
  revenue: {
    gross_revenue_krw: number;
    refund_krw: number;
    net_revenue_krw: number;
    paid_order_count: number;
    daily_series: UsageCalculationRevenueDailyPoint[];
  };
}

export interface UsageCalculationRevenueDailyPoint {
  date: string;
  net_revenue_krw: number;
  order_count: number;
}

export interface UsageCalculationParams {
  mode?: 'period' | 'month';
  period_days?: number;
  year?: number;
  month?: number;
  start_date?: string;
  end_date?: string;
  recent_limit?: number;
}

export async function getAdminUsageCalculationPage(
  params: UsageCalculationParams,
  signal?: AbortSignal
): Promise<UsageCalculationData> {
  return adminGet('/api/admin/usage-calculation', {
    params,
    signal,
  });
}

export interface UsageLogsPageParams {
  mode?: 'period' | 'month' | 'custom';
  period_days?: number;
  year?: number;
  month?: number;
  start_date?: string;
  end_date?: string;
  kind: 'generation' | 'import';
  user_search?: string;
  question_type?: string;
  difficulty?: string;
  /** Minimum cost/charge ratio in percent for generation logs. */
  cost_ratio_min?: number;
  page: number;
  page_size: number;
  /** Boundary echoed back from a previous response so paging stays on one
   * frozen snapshot (log rows are append-only and ordered newest-first, so
   * a live list shifts every OFFSET window down mid-session). */
  watermark_created_at?: string;
  watermark_id?: number;
}

export interface UsageLogWatermark {
  created_at: string;
  id: number;
}

export interface UsageLogFilterOption {
  key: string;
  label: string;
  count: number;
}

export interface UsageLogsPageResponse {
  range: { start: string; end: string };
  kind: 'generation' | 'import';
  watermark: UsageLogWatermark | null;
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  items: UsageCalculationGenerationLog[] | UsageCalculationImportLog[];
  filters: {
    question_types: UsageLogFilterOption[];
    difficulties: UsageLogFilterOption[];
    question_type_total?: number;
    difficulty_total?: number;
  };
}

/** One page of raw usage logs covering the WHOLE filter range (the aggregate
 * payload's `recent_logs` is only a capped preview). */
export async function getAdminUsageLogsPage(
  params: UsageLogsPageParams,
  signal?: AbortSignal
): Promise<UsageLogsPageResponse> {
  return adminGet('/api/admin/usage-calculation/logs', { params, signal });
}

export interface UsageCalculationLogPayload {
  job_audits?: Array<{
    id: string; event: string; http_status: number;
    actor_user_id: number | null; owner_user_id: number | null;
    created_at: string; details: Record<string, unknown>;
  }>;
  recovery_captures?: Array<{
    id: number;
    stream_seq: number;
    result_text: string;
    provider_completed: boolean;
  }>;
  usage_log_id: number;
  input_text: string | null;
  result_text: string | null;
  created_at: string;
}

/** Input/result text for one generation log row (404 → null: pre-feature
 * row, no text payload, or pruned by the 3-month retention). */
export async function getUsageCalculationLogPayload(
  logId: number,
  signal?: AbortSignal
): Promise<UsageCalculationLogPayload | null> {
  try {
    return await adminGet(`/api/admin/usage-calculation/logs/${logId}/payload`, { signal });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export interface UsageCalculationCall {
  seq: number;
  stage_label: string | null;
  provider: string;
  model: string;
  effort_or_thinking: string;
  duration_ms: number;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_write: number | null;
  cache_read: number | null;
  reasoning_tokens: number | null;
  cost_usd: number | null;
  cost_krw: number | null;
}

/** Per-AI-call ledger rows for one generation log row. Empty list when the
 * request predates per-call recording or was pruned by the 90-day retention. */
export async function getUsageCalculationLogCalls(
  logId: number,
  signal?: AbortSignal
): Promise<UsageCalculationCall[]> {
  const data = await adminGet<{ calls?: UsageCalculationCall[] }>(
    `/api/admin/usage-calculation/logs/${logId}/calls`,
    { signal }
  );
  return Array.isArray(data?.calls) ? data.calls : [];
}

export async function getAdminUsersPage(
  params: AdminUsersPageParams,
  signal?: AbortSignal
): Promise<AdminUsersPageResponse> {
  try {
    return normalizeUsersPageData(
      await adminGet('/api/admin/users-page', {
        params,
        signal,
      })
    );
  } catch (error) {
    if (!isMissingBffEndpoint(error)) {
      throw error;
    }

    const [usersData, preferencesData] = await Promise.all([
      adminGet('/api/admin/users', { params, signal }),
      adminGet('/api/user/preferences', { signal }).catch(() => null),
    ]);

    return normalizeUsersPageData({
      ...asRecord(usersData),
      preferences: asRecord(preferencesData).preferences ?? asRecord(preferencesData),
    });
  }
}

export async function getAdminUserDetailPage(
  userId: number,
  params: AdminUserDetailPageParams,
  signal?: AbortSignal
): Promise<AdminUserDetailPageResponse> {
  try {
    return normalizeUserDetailPageData(
      await adminGet(`/api/admin/users/${userId}/page`, {
        params,
        signal,
      })
    );
  } catch (error) {
    if (!isMissingBffEndpoint(error)) {
      throw error;
    }

    return normalizeUserDetailPageData(
      await adminGet(`/api/admin/users/${userId}`, {
        params,
        signal,
      })
    );
  }
}

export async function getAdminOrdersPage(
  params: AdminOrdersPageParams,
  signal?: AbortSignal
): Promise<AdminOrdersPageResponse> {
  try {
    return normalizeOrdersPageData(
      await adminGet('/api/admin/orders-page', {
        params,
        signal,
      })
    );
  } catch (error) {
    if (!isMissingBffEndpoint(error)) {
      throw error;
    }

    return normalizeOrdersPageData(
      await adminGet('/api/admin/orders', {
        params,
        signal,
      })
    );
  }
}

export interface CronLog {
  id: number;
  job_name: string;
  status: string;
  summary_json: Record<string, number> | null;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string | null;
}

export interface CronLogsResponse {
  logs: CronLog[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export async function getAdminCronLogs(
  params: { page: number; per_page: number; job_name?: string },
  signal?: AbortSignal
): Promise<CronLogsResponse> {
  return adminGet('/api/admin/cron-logs', { params, signal });
}

export interface AdminBadgeCounts {
  pending_refunds: number;
  unread_chats: number;
}

export async function getAdminBadgeCounts(
  signal?: AbortSignal
): Promise<AdminBadgeCounts> {
  return adminGet('/api/admin/badge-counts', { signal });
}

export interface ProductImageUploadResponse {
  success: boolean;
  image_url: string;
}

export interface ProductImage {
  url: string;
  product_name: string | null;
}

export interface ProductImagesResponse {
  images: ProductImage[];
}

export async function uploadProductImage(
  productId: number,
  file: File
): Promise<ProductImageUploadResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post<ProductImageUploadResponse>(
    `/api/admin/products/${productId}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function getProductImages(
  signal?: AbortSignal
): Promise<ProductImagesResponse> {
  return adminGet('/api/admin/product-images', { signal });
}

export async function setProductImageUrl(
  productId: number,
  imageUrl: string
): Promise<{ success: boolean }> {
  const response = await api.put<{ success: boolean }>(
    `/api/admin/products/${productId}/image-url`,
    { image_url: imageUrl }
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Refund Quote (SPEC-REFUND-QUOTE-001 Task 3.3)
// ---------------------------------------------------------------------------

export interface RefundQuoteUsageBreakdown {
  credits_used: number;
  credit_unit_price_krw: number;
  credit_usage_krw: number;
  file_usage_log_sum: number;
  file_usage_log_items: unknown[];
}

export interface RefundQuoteSubscription {
  id: number;
  effective_start_kst: string;
  effective_end_kst: string;
  total_days: number;
  is_renewal: boolean;
  schedule_end_source: string;
}

// @MX:ANCHOR: [AUTO] Core refund calculation result shape — consumed by RefundBreakdownTable, SubscriptionCancelRefundModal, and AdminOrderBoard
// @MX:REASON: Fan-in >= 3: breakdown table + modal + order board all read this type
export interface RefundQuote {
  order_id: number;
  calculation_error: boolean;
  multiple_active_subscriptions: boolean;
  error_code?: string;
  fallback_amount?: number;
  fallback_amount_source?: string;
  // when calculation_error = false:
  original_paid_amount: number;
  already_refunded: number;
  max_remaining_refundable: number;
  currency: 'KRW';
  paid_at_kst: string | null;
  subscription: RefundQuoteSubscription | null;
  days_elapsed: number;
  total_days: number;
  time_portion: number;
  usage_portion: number;
  usage_breakdown: RefundQuoteUsageBreakdown | null;
  refund_amount_this_request: number;
  refundable: boolean;
  unused_renewal: boolean;
  remaining_credits_to_revoke: number;
  pricing_source: 'order_snapshot' | 'system_settings' | 'code_fallback';
  calculated_at_kst: string | null;
  terms_version_at_payment?: string;
  terms_version_source?: 'order_snapshot' | 'current_setting';
}

export async function getRefundQuote(
  orderId: number,
  signal?: AbortSignal
): Promise<RefundQuote> {
  // Endpoint registered under store_bp(/api/store) in routes/store_routes.py:3331.
  // Admins are authorized via check_order_access() inside the handler.
  return adminGet(`/api/store/orders/${orderId}/refund-quote`, { signal });
}

export interface ProcessMemoryStatus {
  memory_used_mb: number;
  memory_limit_mb: number;
  memory_remaining_mb: number;
  memory_usage_percent: number;
  pid?: number;
  error?: string;
}

export interface AdminMemoryStatusResponse {
  web: ProcessMemoryStatus;
  worker: ProcessMemoryStatus | null;
  worker_error: string | null;
  guard_threshold_percent: number;
}

export async function getAdminMemoryStatus(
  signal?: AbortSignal
): Promise<AdminMemoryStatusResponse> {
  return adminGet('/api/admin/memory-status', { signal });
}
